import { Button } from '@/components/_ui/Button'
import Image from 'next/image'
import { useModals } from '@/components/ModalManager/useModals'

import type { ExclusiveLockedContentType } from '@/lib/cms/cosmic/types/CosmicProductType'
import Link from 'next/link'
import useParseLinkUrlWithLocale from '@/lib/hooks/routing/useParseLinkUrlWithLocale'
import type { CartItemType } from '@/store/types/cart'
import { mixpanelService } from '@/lib/services/mixpanel/initializeMixPanel'
import { MixpanelEvent } from '@/lib/services/mixpanel/mixpanelEvent'
import { ErrorAlert } from '@/components/_ui/ErrorAlert'
import { Skeleton } from '@/components/_ui/Skeleton'
import { useGetExclusiveContent } from '@/lib/hooks/services/useGetExclusiveContent'

type Props = {
  exclusiveLockedContentData?: ExclusiveLockedContentType | null
  itemLocked: CartItemType
}

export const ExclusiveProductLock = ({
  exclusiveLockedContentData,
  itemLocked,
}: Props) => {
  const { modals } = useModals()
  const { parseLinkUrlWithLocale } = useParseLinkUrlWithLocale()

  const {
    exclusiveContentData: clientSideFetchedExclusiveContentData,
    isLoading: isLoadingExclusiveContent,
  } = useGetExclusiveContent()

  const openExclusiveProductLockModal = () => {
    modals.openModal({
      type: 'modal',
      modalId: 'exclusive-product-lock',
      modal: {
        className: 'max-w-full w-[768px]',
        children: (
          <div className="flex flex-col items-center gap-10 sm:flex-row">
            <Image
              src={exclusiveLockedContentData?.learnMore.image.url ?? ''}
              alt={exclusiveLockedContentData?.learnMore.image.alt ?? ''}
              width={340}
              height={405}
              // Seems like WET code for height, but it's necessary due to Tailwind's global h-auto, globally, on image
              className="h-[405px] rounded-xl"
            />

            <div className="flex-1">
              <h3 className="text-2xl font-semibold">
                {exclusiveLockedContentData?.learnMore.title}
              </h3>

              <ul className="mt-5 list-disc pl-5 marker:mr-2 marker:[content:url('/icons/checked-circle-filled-blue.svg')]">
                {exclusiveLockedContentData?.learnMore.steps.map((step) => (
                  <li
                    key={step}
                    className="pl-2 text-sm leading-6 font-normal"
                    dangerouslySetInnerHTML={{
                      __html: step || '',
                    }}
                  />
                ))}
              </ul>

              <h4 className="mt-4 mb-3 font-bold">
                {exclusiveLockedContentData?.learnMore.footerText}
              </h4>

              <p className="mb-5 text-sm">
                {exclusiveLockedContentData?.learnMore.subtitle}
              </p>

              <Button
                className="flex w-full items-center justify-center bg-[#0075C0]"
                onClick={() => {
                  modals.closeModals()
                }}
              >
                <Link
                  href={parseLinkUrlWithLocale({
                    url:
                      exclusiveLockedContentData?.learnMore.button.link ?? '',
                  })}
                  className="no-underline"
                >
                  {exclusiveLockedContentData?.learnMore.button.text}
                </Link>
              </Button>
            </div>
          </div>
        ),
      },
    })
  }

  // Only show loader if no data came from product grid.
  if (isLoadingExclusiveContent && !exclusiveLockedContentData) {
    return (
      <div>
        <Skeleton className="h-40 w-full rounded-xl border" />
      </div>
    )
  }

  if (!exclusiveLockedContentData && !clientSideFetchedExclusiveContentData) {
    return (
      <ErrorAlert
        error={{
          error: {
            message: 'Exclusive product lock data is missing',
          },
        }}
      />
    )
  }

  const data =
    exclusiveLockedContentData ?? clientSideFetchedExclusiveContentData

  return (
    <div
      data-test="exclusive-product-lock"
      className="relative flex w-full flex-col items-center rounded-xl bg-[#E1F0F3] p-8 px-5"
    >
      <Image
        src="/icons/lock-circle.svg"
        alt="Lock"
        height={40}
        width={40}
        className="absolute -top-5"
      />

      <h2 className="ml-2.5 text-center text-2xl font-semibold">
        {data?.title}
      </h2>

      <div className="mt-6 flex w-full flex-col rounded-md border border-solid border-[#93CCDC] bg-[#C8ECF2] px-3.5 py-4">
        <div className="flex items-start gap-4 text-lg font-semibold">
          <Image
            src="/icons/star-outline.svg"
            alt="Lock icon"
            width={18}
            height={18}
          />

          <h3 className="text-lg leading-normal font-semibold">
            {data?.alert.title}
          </h3>
        </div>

        {/* TODO: implement this with proper parsing library so that it uses
        a link in the CMS and only that link is clickable */}
        <button
          type="button"
          className="mt-3 ml-9 cursor-pointer text-sm lg:mt-0"
          dangerouslySetInnerHTML={{
            __html: data?.alert.message ?? '',
          }}
          aria-label={data?.alert.title || 'Open exclusive product lock modal'}
          onClick={() => {
            openExclusiveProductLockModal()
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              openExclusiveProductLockModal()
            }
          }}
        />
      </div>

      <Button
        className="mt-4 flex w-full items-center justify-center"
        onClick={() => {
          openExclusiveProductLockModal()
          mixpanelService
            .trackEvent(MixpanelEvent.UNLOCK_PRODUCT_DETAIL_CLICKED, {
              item_brand: itemLocked.product.flag?.label || 'Unicity',
              item_sku: itemLocked.product.sku,
              item_name: itemLocked.product.combinationName,
            })
            .catch(() => {})
        }}
      >
        <Image
          src="/icons/cart-white.svg"
          alt="Unlock"
          width={17}
          height={14}
          className="mr-2 shrink-0"
        />
        <span className="flex-1 text-center">{data?.button.text}</span>
      </Button>
    </div>
  )
}
